"use client";

/**
 * "Previous listings": the cards a quiet residence had that are gone now
 * (/api/listings?status=removed&sort=gone), each dated with when it left,
 * under one line that says when the last one went. A residence with
 * nothing on the market used to show nothing here at all, so a visitor
 * could not tell a sold-out building from one never covered (feedback
 * ticket 34, 2026-10-18). Cards open the listing's own page, which says
 * "removed" first and keeps every fact; the live drawer would only say
 * it is gone.
 */
import { useRouter } from "next/navigation";
import ListingBrowseCard from "@/components/listings/ListingBrowseCard";
import { useT, useLocale } from "@/lib/locale-context";
import { formatMonthDayShort } from "@/lib/blog-format";
import { listingPath } from "@/lib/seo/paths";
import type { ListingCard } from "@/types";

/** Two rows of three on desktop, two of two on phones. */
const MAX_CARDS = 6;

function fill(s: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, String(v)), s);
}

export default function PreviousListings({
  cards,
  lastSeenAt,
  children,
}: {
  cards: ListingCard[];
  /** ISO date the newest departure was last seen (summary.previous). */
  lastSeenAt?: string | null;
  /** Rendered under the intro line: the follow / alert hook. */
  children?: React.ReactNode;
}) {
  const t = useT();
  const { locale } = useLocale();
  const router = useRouter();
  if (cards.length === 0) return null;
  const shown = cards.slice(0, MAX_CARDS);
  const when = (iso: string | null | undefined): string | null =>
    iso ? formatMonthDayShort(Date.parse(iso), locale) : null;
  const last = when(lastSeenAt) ?? when(shown[0]?.expired_at);
  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-gray-900">{t("residence.previous.title")}</h2>
      <p className="mt-1 text-sm text-gray-600">
        {last ? fill(t("residence.previous.sub"), { date: last }) : t("residence.previous.sub_nodate")}
      </p>
      {children && <div className="mt-3">{children}</div>}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 auto-rows-fr gap-3">
        {shown.map((listing, i) => {
          const lp = listingPath(listing.id);
          const href = lp ? `/${locale}${lp}` : undefined;
          const gone = when(listing.expired_at);
          return (
            <div
              key={listing.id}
              // Phones get 2x2; the third row only exists from sm up.
              className={i >= 4 ? "hidden sm:flex sm:flex-col" : "flex flex-col"}
            >
              <div className="flex-1">
                <ListingBrowseCard
                  listing={listing}
                  href={href}
                  onOpen={() => { if (href) router.push(href); }}
                />
              </div>
              <p className="mt-1.5 text-[11px] text-gray-500">
                {gone ? fill(t("residence.previous.gone"), { date: gone }) : t("residence.previous.gone_nodate")}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
